import React, { useState, useEffect } from 'react';
import Masonry from 'react-masonry-css';
import ProductListItem from '../components/ProductListItem';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LmHeader from '../components/LmHeader.jsx';
import LmFooter from '../components/LmFooter.jsx';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('АКТИВНО');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [stats, setStats] = useState({ total: 0, active: 0, archived: 0, cancelled: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: 12, total: 0, pages: 0 });
  const [addProductCode, setAddProductCode] = useState('');
  const [foundProduct, setFoundProduct] = useState(null);
  const [searchingProduct, setSearchingProduct] = useState(false);
  const [addingProduct, setAddingProduct] = useState(false);
  const [requestingTransfer, setRequestingTransfer] = useState(false);
  const [transferRequests, setTransferRequests] = useState([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [updatingTransfer, setUpdatingTransfer] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('clientToken');
    const clientData = localStorage.getItem('clientData');

    if (!token || !clientData) {
      navigate('/enter');
      return;
    }

    try {
      const parsedClient = JSON.parse(clientData);
      setClient(parsedClient);
      loadProducts();
    } catch (error) {
      console.error('Ошибка парсинга данных клиента:', error);
      navigate('/enter');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Загружаем запросы на передачу после загрузки клиента
  useEffect(() => {
    if (client?.id) {
      loadTransferRequests();
    }
  }, [client?.id]);

  // Подтверждение получения изделия (для нового владельца)
  const confirmReceipt = async (requestId) => {
    try {
      setUpdatingTransfer(true);
      const token = localStorage.getItem('clientToken');

      const response = await axios.put(`https://limitverify.ru:5000/api/transfers/${requestId}/confirm-receipt`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        alert('Получение изделия подтверждено! Изделие передано в ваш кабинет.');
        // Обновляем список запросов
        loadTransferRequests();
        // Обновляем список изделий
        loadProducts(pagination.page, searchTerm, statusFilter, sortBy, sortOrder);
      } else {
        alert('Ошибка подтверждения получения');
      }
    } catch (error) {
      console.error('Ошибка подтверждения получения:', error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('Ошибка подтверждения получения. Попробуйте позже.');
      }
    } finally {
      setUpdatingTransfer(false);
    }
  };

  // Обновление статуса запроса на передачу
  const updateTransferRequestStatus = async (requestId, status, rejectionReason = null) => {
    try {
      setUpdatingTransfer(true);
      const token = localStorage.getItem('clientToken');

      const response = await axios.put(`https://limitverify.ru:5000/api/transfers/${requestId}/status`, {
        status,
        rejectionReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        alert(`Запрос на передачу ${status === 'ПОДТВЕРЖДЕНО' ? 'подтвержден' : 'отклонен'}!`);
        // Обновляем список запросов
        loadTransferRequests();
        // Обновляем список изделий
        loadProducts(pagination.page, searchTerm, statusFilter, sortBy, sortOrder);
      } else {
        alert('Ошибка обновления статуса запроса');
      }
    } catch (error) {
      console.error('Ошибка обновления статуса запроса:', error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('Ошибка обновления статуса запроса. Попробуйте позже.');
      }
    } finally {
      setUpdatingTransfer(false);
    }
  };

  // Загрузка запросов на передачу
  const loadTransferRequests = async () => {
    if (!client?.id) return;

    try {
      const token = localStorage.getItem('clientToken');

      // Загружаем запросы где клиент является отправителем (fromClientId) - исходящие запросы
      const sentResponse = await axios.get(`https://limitverify.ru:5000/api/transfers/client?type=sent&status=ЗАПРОС ПЕРЕДАЧИ`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Загружаем подтвержденные запросы где клиент является отправителем (fromClientId) - исходящие подтвержденные
      const sentConfirmedResponse = await axios.get(`https://limitverify.ru:5000/api/transfers/client?type=sent&status=ПОДТВЕРЖДЕНО`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Загружаем подтвержденные запросы где клиент является получателем (toClientId) - входящие подтвержденные
      const receivedResponse = await axios.get(`https://limitverify.ru:5000/api/transfers/client?type=received&status=ПОДТВЕРЖДЕНО`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const sentRequests = sentResponse.data.success ? sentResponse.data.transferRequests || [] : [];
      const sentConfirmedRequests = sentConfirmedResponse.data.success ? sentConfirmedResponse.data.transferRequests || [] : [];
      const receivedRequests = receivedResponse.data.success ? receivedResponse.data.transferRequests || [] : [];

      // Объединяем все запросы
      const allRequests = [...sentRequests, ...sentConfirmedRequests, ...receivedRequests];
      console.log('Загружены запросы на передачу:', allRequests);
      setTransferRequests(allRequests);
    } catch (error) {
      console.error('Ошибка загрузки запросов на передачу:', error);
      setTransferRequests([]);
    }
  };

  const loadProducts = async (page = 1, search = searchTerm, status = statusFilter, sortField = sortBy, sortDirection = sortOrder) => {
    setProductsLoading(true);
    try {
      const token = localStorage.getItem('clientToken');
      const response = await axios.get('https://limitverify.ru:5000/api/client/products', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit: 12,
          search,
          status,
          sortBy: sortField,
          sortOrder: sortDirection
        }
      });

      setProducts(response.data.products);
      setStats(response.data.stats);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Ошибка загрузки изделий:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    loadProducts(1, searchTerm, statusFilter);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPagination(prev => ({ ...prev, page: 1 }));
    loadProducts(1, searchTerm, status);
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
    loadProducts(page, searchTerm, statusFilter);
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('АКТИВНО');
    setSortBy('created_at');
    setSortOrder('DESC');
    setPagination(prev => ({ ...prev, page: 1 }));
    loadProducts(1, '', 'АКТИВНО', 'created_at', 'DESC');
  };

  // Поиск изделия по коду
  const searchProductByCode = async () => {
    if (!addProductCode.trim()) {
      alert('Введите код изделия');
      return;
    }

    setSearchingProduct(true);
    setFoundProduct(null);

    try {
      const token = localStorage.getItem('clientToken');
      const response = await axios.get(`https://limitverify.ru:5000/api/client/search-product`, {
        params: { code: addProductCode.trim() },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.product) {
        setFoundProduct(response.data.product);
      } else {
        alert('Изделие с таким кодом не найдено или уже имеет владельца');
      }
    } catch (error) {
      console.error('Ошибка поиска изделия:', error);
      if (error.response?.status === 404) {
        alert('Изделие с таким кодом не найдено или уже имеет владельца');
      } else {
        alert('Ошибка поиска изделия. Попробуйте позже.');
      }
    } finally {
      setSearchingProduct(false);
    }
  };

  // Добавление изделия в кабинет
  const addProductToCabinet = async () => {
    if (!foundProduct) return;

    setAddingProduct(true);

    try {
      const token = localStorage.getItem('clientToken');
      const response = await axios.post(`https://limitverify.ru:5000/api/client/add-product`, {
        productId: foundProduct.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        alert('Изделие успешно добавлено в ваш кабинет!');
        setAddProductCode('');
        setFoundProduct(null);
        // Обновляем список изделий (статистика обновится автоматически)
        loadProducts(pagination.page, searchTerm, statusFilter, sortBy, sortOrder);
        loadTransferRequests();
      } else {
        alert('Ошибка добавления изделия');
      }
    } catch (error) {
      console.error('Ошибка добавления изделия:', error);
      alert('Ошибка добавления изделия. Попробуйте позже.');
    } finally {
      setAddingProduct(false);
    }
  };

  // Отмена добавления изделия
  const cancelAddProduct = () => {
    setFoundProduct(null);
    setAddProductCode('');
  };

  // Запрос на передачу изделия
  const requestProductTransfer = async () => {
    if (!foundProduct) return;

    setRequestingTransfer(true);

    try {
      const token = localStorage.getItem('clientToken');
      const response = await axios.post(`https://limitverify.ru:5000/api/client/request-transfer`, {
        productId: foundProduct.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        alert('Запрос на передачу изделия отправлен успешно!');
        setAddProductCode('');
        setFoundProduct(null);
        // Обновляем список изделий
        loadProducts(pagination.page, searchTerm, statusFilter, sortBy, sortOrder);
        loadTransferRequests();
      } else {
        alert('Ошибка отправки запроса на передачу');
      }
    } catch (error) {
      console.error('Ошибка запроса передачи:', error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('Ошибка отправки запроса на передачу. Попробуйте позже.');
      }
    } finally {
      setRequestingTransfer(false);
    }
  };

  // Проверяем, есть ли активные фильтры
  const hasActiveFilters = searchTerm || statusFilter !== 'АКТИВНО' || sortBy !== 'created_at' || sortOrder !== 'DESC';

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('clientToken');
      if (token) {
        await axios.post('https://limitverify.ru:5000/api/client/logout', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Ошибка при выходе:', error);
    } finally {
      localStorage.removeItem('clientToken');
      localStorage.removeItem('clientData');
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="lm-page">
        <main className="lm-content lm-content--center">
          <span className="lm-text-2 lm-muted">Загрузка...</span>
        </main>
      </div>
    );
  }

  if (!client) {
    return null;
  }

  const stat = (value, label, color) => (
    <div
      style={{
        flex: 1,
        minWidth: 90,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: '12px 10px',
        border: '1px solid var(--border-color)',
        background: 'var(--bg-control)'
      }}
    >
      <span className="lm-h3" style={{ color }}>{value}</span>
      <span className="lm-descriptor lm-muted">{label}</span>
    </div>
  );

  const statuses = ['all', 'АКТИВНО', 'АРХИВ', 'АННУЛИРОВАНО'];

  return (
    <div className="lm-page">
      <LmHeader
        logoTo="/cabinet"
        actions={[
          { label: 'Профиль', onClick: () => navigate('/cabinet/profile') },
          { label: 'Выйти', onClick: handleLogout }
        ]}
      />

      <main className="lm-content">
        <h1 className="lm-h1" style={{ textAlign: 'center' }}>Мои изделия</h1>

        {/* Уведомление о запросах на передачу */}
        {transferRequests.length > 0 && (
          <button
            type="button"
            onClick={() => setShowTransferModal(true)}
            className="lm-section"
            style={{
              cursor: 'pointer',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              borderColor: 'var(--accent)',
              textAlign: 'left'
            }}
          >
            <span
              style={{
                width: 28,
                height: 28,
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--accent)',
                color: 'var(--accent)'
              }}
            >
              !
            </span>
            <span className="lm-text-3" style={{ color: 'var(--text-primary)' }}>
              У вас есть {transferRequests.length}{' '}
              {transferRequests.length === 1 ? 'запрос' : transferRequests.length < 5 ? 'запроса' : 'запросов'} на передачу{' '}
              {transferRequests.length === 1 ? 'изделия' : 'изделий'}
            </span>
          </button>
        )}

        {/* Статистика + добавление изделия */}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <section className="lm-section" style={{ flex: 1, minWidth: 300 }}>
            <h3 className="lm-h3">Статистика изделий</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {stat(stats.total, 'Всего', 'var(--accent)')}
              {stat(stats.active, 'Активных', 'var(--accent)')}
              {stat(stats.archived, 'Архив', 'var(--text-primary)')}
              {stat(stats.cancelled, 'Аннулир.', 'var(--danger)')}
            </div>
          </section>

          <section className="lm-section" style={{ flex: 1, minWidth: 300 }}>
            <h3 className="lm-h3">Добавить изделие</h3>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="lm-field" style={{ flex: 1, minWidth: 160 }}>
                <span className="lm-field__label">Код изделия</span>
                <input
                  type="text"
                  className="lm-field__input"
                  placeholder="Введите код изделия"
                  value={addProductCode}
                  onChange={(e) => setAddProductCode(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchProductByCode()}
                  disabled={searchingProduct}
                />
              </div>
              <button
                type="button"
                className="lm-btn"
                onClick={searchProductByCode}
                disabled={searchingProduct || !addProductCode.trim()}
              >
                {searchingProduct ? 'Поиск...' : 'Найти'}
              </button>
            </div>

            {foundProduct && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  padding: 16,
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-control)'
                }}
              >
                <span className="lm-descriptor lm-muted">Найдено изделие</span>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      flexShrink: 0,
                      background: 'var(--bg-page)',
                      border: '1px solid var(--border-color)',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {foundProduct.mainPhoto ? (
                      <img src={foundProduct.mainPhoto} alt={foundProduct.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span className="lm-descriptor-2 lm-muted">нет фото</span>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <FoundRow label="Название" value={foundProduct.name} />
                    {foundProduct.serialNumber && <FoundRow label="Серийный" value={foundProduct.serialNumber} />}
                    <FoundRow label="ID" value={foundProduct.identificationNumber} />
                    {foundProduct.features && <FoundRow label="Особенности" value={foundProduct.features} />}
                    {foundProduct.description && <FoundRow label="Описание" value={foundProduct.description} />}
                  </div>
                </div>

                {foundProduct.hasOwner && foundProduct.currentOwner && (
                  <div
                    style={{
                      padding: 12,
                      border: '1px solid var(--danger)',
                      background: 'var(--color-dark-red-100)'
                    }}
                  >
                    <div className="lm-text-3 lm-danger" style={{ marginBottom: 4 }}>
                      Изделие уже имеет владельца
                    </div>
                    <div className="lm-descriptor">
                      <span className="lm-muted">ФИО: </span>
                      {foundProduct.currentOwner.name}
                    </div>
                    {foundProduct.currentOwner.ownerSince && (
                      <div className="lm-descriptor">
                        <span className="lm-muted">Владеет с: </span>
                        {new Date(foundProduct.currentOwner.ownerSince).toLocaleDateString('ru-RU')}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {foundProduct.canAdd && (
                    <button type="button" className="lm-btn" onClick={addProductToCabinet} disabled={addingProduct}>
                      {addingProduct ? 'Добавление...' : 'Добавить в кабинет'}
                    </button>
                  )}
                  {foundProduct.canRequestTransfer && (
                    <button type="button" className="lm-btn" onClick={requestProductTransfer} disabled={requestingTransfer}>
                      {requestingTransfer ? 'Отправка...' : 'Запросить передачу'}
                    </button>
                  )}
                  <button type="button" className="lm-btn lm-btn--danger" onClick={cancelAddProduct} disabled={addingProduct || requestingTransfer}>
                    Отмена
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Поиск и фильтры */}
        <section className="lm-section">
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="lm-field" style={{ flex: 1, minWidth: 220 }}>
              <span className="lm-field__label">Поиск</span>
              <input
                type="text"
                className="lm-field__input"
                placeholder="Серийный / идентификационный номер или название"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button type="submit" className="lm-btn">Найти</button>
            <button
              type="button"
              className={hasActiveFilters ? 'lm-btn lm-btn--danger' : 'lm-btn'}
              onClick={handleResetFilters}
            >
              {hasActiveFilters ? 'Сбросить фильтры' : 'Сбросить'}
            </button>
          </form>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span className="lm-field__label">Фильтр по статусу</span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {statuses.map(status => {
                const active = statusFilter === status;
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => handleStatusFilter(status)}
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--fs-text-3)',
                      textTransform: 'uppercase',
                      background: active ? 'var(--color-dark-green-100)' : 'transparent',
                      color: active ? 'var(--accent)' : 'var(--text-muted)',
                      border: `1px solid ${active ? 'var(--accent)' : 'var(--border-color)'}`
                    }}
                  >
                    {status === 'all' ? 'Все' : status}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Список изделий */}
        {productsLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <span className="lm-text-2 lm-muted">Загрузка изделий...</span>
          </div>
        ) : products.length === 0 ? (
          <section className="lm-section" style={{ alignItems: 'center', textAlign: 'center', padding: 40 }}>
            <h3 className="lm-h3">У вас пока нет закреплённых изделий</h3>
            <p className="lm-text-3 lm-muted" style={{ margin: 0, maxWidth: 400 }}>
              Чтобы добавить изделия, используйте проверку кода или дождитесь приглашения.
            </p>
          </section>
        ) : (
          <>
            <style>{`
              .masonry-grid {
                display: flex;
                margin-left: -24px;
                width: auto;
                align-items: flex-start;
              }
              .masonry-grid-column {
                padding-left: 24px;
                background-clip: padding-box;
                display: flex;
                flex-direction: column;
                align-items: stretch;
              }
              .masonry-item {
                margin-bottom: 24px;
                width: 100%;
                height: auto !important;
              }
              .lm-product-card {
                height: auto !important;
              }
              @media (max-width: 480px) {
                .masonry-grid { margin-left: -16px; }
                .masonry-grid-column { padding-left: 16px; }
                .masonry-item { margin-bottom: 16px; }
              }
            `}</style>
            <Masonry
              breakpointCols={{ default: 3, 850: 2, 600: 1 }}
              className="masonry-grid"
              columnClassName="masonry-grid-column"
            >
              {products.map(product => {
                const hasTransferRequest = transferRequests.some(request =>
                  request.productId === product.id && request.status === 'ЗАПРОС ПЕРЕДАЧИ'
                );
                const hasConfirmedRequest = transferRequests.some(request =>
                  request.productId === product.id && request.status === 'ПОДТВЕРЖДЕНО'
                );

                return (
                  <ProductListItem
                    key={product.id}
                    product={product}
                    onClick={() => navigate(`/product/${product.id}`)}
                    hasTransferRequest={hasTransferRequest}
                    hasConfirmedRequest={hasConfirmedRequest}
                  />
                );
              })}
            </Masonry>

            {pagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="lm-btn"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Назад
                </button>

                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => {
                  const active = pagination.page === page;
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => handlePageChange(page)}
                      style={{
                        minWidth: 46,
                        height: 'var(--control-height)',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-mono)',
                        background: active ? 'var(--color-dark-green-100)' : 'var(--bg-control)',
                        color: active ? 'var(--accent)' : 'var(--text-muted)',
                        border: `1px solid ${active ? 'var(--accent)' : 'var(--border-color)'}`
                      }}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  type="button"
                  className="lm-btn"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                >
                  Вперёд
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <LmFooter />

      {/* Модальное окно для запросов на передачу */}
      {showTransferModal && (
        <div className="lm-modal-overlay">
          <div className="lm-modal">
            <button type="button" className="lm-modal__close" onClick={() => setShowTransferModal(false)} aria-label="Закрыть">
              ✕
            </button>
            <h3 className="lm-modal__title">Запросы на передачу изделий</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {transferRequests.map(request => (
                <div
                  key={request.id}
                  style={{
                    padding: 16,
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-control)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div className="lm-text-3" style={{ color: 'var(--text-primary)', marginBottom: 4 }}>
                        {request.product.name}
                      </div>
                      <div className="lm-descriptor lm-muted">
                        ID: {request.product.identificationNumber} | Серийный: {request.product.serialNumber}
                      </div>
                    </div>
                    <span
                      className="lm-descriptor-2"
                      style={{
                        alignSelf: 'flex-start',
                        padding: '4px 8px',
                        border: '1px solid var(--accent)',
                        color: 'var(--accent)'
                      }}
                    >
                      {request.status}
                    </span>
                  </div>

                  <div className="lm-descriptor lm-muted" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {request.status === 'ЗАПРОС ПЕРЕДАЧИ' ? (
                      <>
                        <div>Запрос от: <span style={{ color: 'var(--text-primary)' }}>{request.toClient.full_name}</span></div>
                        <div>Дата запроса: {new Date(request.requestDate).toLocaleDateString('ru-RU')}</div>
                      </>
                    ) : (
                      <>
                        <div>Подтверждено от: <span style={{ color: 'var(--text-primary)' }}>{request.fromClient.full_name}</span></div>
                        <div>Дата подтверждения: {request.responseDate ? new Date(request.responseDate).toLocaleDateString('ru-RU') : 'Не указана'}</div>
                        {client.id === request.fromClientId ? (
                          <div className="lm-accent" style={{ marginTop: 4 }}>
                            Ожидает подтверждения получения от {request.toClient.full_name}
                          </div>
                        ) : (
                          <div className="lm-accent" style={{ marginTop: 4 }}>
                            Запрос подтверждён! Готов к получению
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {request.status === 'ЗАПРОС ПЕРЕДАЧИ' ? (
                      <>
                        <button type="button" className="lm-btn" onClick={() => updateTransferRequestStatus(request.id, 'ПОДТВЕРЖДЕНО')} disabled={updatingTransfer}>
                          {updatingTransfer ? 'Обработка...' : 'Подтвердить'}
                        </button>
                        <button
                          type="button"
                          className="lm-btn lm-btn--danger"
                          onClick={() => {
                            const reason = prompt('Причина отклонения (необязательно):');
                            updateTransferRequestStatus(request.id, 'ОТКЛОНЕНО', reason);
                          }}
                          disabled={updatingTransfer}
                        >
                          Отклонить
                        </button>
                      </>
                    ) : client.id === request.fromClientId ? (
                      <span className="lm-descriptor lm-muted">
                        Запрос подтверждён • ожидает подтверждения получения
                      </span>
                    ) : (
                      <button type="button" className="lm-btn" onClick={() => confirmReceipt(request.id)} disabled={updatingTransfer}>
                        {updatingTransfer ? 'Обработка...' : 'Подтвердить получение'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FoundRow = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      gap: 6,
      padding: '6px 8px',
      border: '1px solid var(--border-color)',
      background: 'var(--bg-page)'
    }}
  >
    <span className="lm-descriptor-2 lm-muted" style={{ minWidth: 70 }}>{label}</span>
    <span className="lm-descriptor" style={{ color: 'var(--text-primary)' }}>{value}</span>
  </div>
);

export default ClientDashboard;
